import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import type { ImageBannerWidgetDto } from "../../../types/dtos/content-dashboard/ContentDashboardWidgetDto";
import Popover from "../../common/Popover";
import SafeRenderHtml from "../../common/SafeRenderHtml";
import Tooltip from "../../common/Tooltip";
import imageHelper from "../../../helpers/imageHelper";
import { captionHtmlForDisplay } from "../../../helpers/contentDashboardCaption";

interface ImageBannerWidgetCardProps {
  widget: ImageBannerWidgetDto;
  fullWidth: boolean;
}

const ImageBannerWidgetCard: React.FC<ImageBannerWidgetCardProps> = ({
  widget,
  fullWidth,
}) => {
  const [isHelpPopoverOpen, setIsHelpPopoverOpen] = useState(false);
  const caption = captionHtmlForDisplay(widget.captionHtml);
  const imageUrl = widget.imageFilename
    ? imageHelper.getImagePath(widget.imageFilename, fullWidth ? 1400 : 800)
    : "";

  if (!widget.imageFilename) {
    return null;
  }

  const image = (
    <img
      src={imageUrl}
      alt={widget.title}
      className={`w-full object-cover rounded-md ${
        fullWidth ? "aspect-video md:aspect-[4/1]" : "aspect-video"
      }`}
    />
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-full flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Tooltip
          content={widget.title}
          appearAt="top"
          triggerElement={
            <p className="font-semibold text-gray-900 flex-grow truncate">{widget.title}</p>
          }
        />
        {widget.helpText && (
          <Popover
            triggerElement={
              <button
                type="button"
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                aria-label="Help"
              >
                <FontAwesomeIcon icon={faCircleInfo} size="sm" />
              </button>
            }
            openState={isHelpPopoverOpen}
            onOpenStateChange={setIsHelpPopoverOpen}
            placement="top"
            showCloseIcon={true}
            darkMode={true}
          >
            <SafeRenderHtml
              htmlText={widget.helpText}
              containerClassName="text-sm text-white leading-relaxed"
            />
          </Popover>
        )}
      </div>

      {widget.destinationUrl ? (
        <a href={widget.destinationUrl} target="_blank" rel="noopener noreferrer">
          {image}
        </a>
      ) : (
        image
      )}

      {caption && (
        <div
          className="underline-links-inside mt-3 text-sm text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: caption }}
        />
      )}
    </div>
  );
};

export default ImageBannerWidgetCard;
